import React, { lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { Dimmer, Loader } from 'semantic-ui-react'
import { IUserDetail } from '../types/user'

// Budget Module Components
const StudyBudgetingContainer = lazy(() => import('./budget/containers/StudyBudgetingWithErrorBoundary'))
const BudgetSettingsContainer = lazy(() => import('./budget/containers/BudgetSettingsContainer'))
const BudgetDetailContainer = lazy(() => import('./budget/components/BudgetDetailContainer'))
const BudgetTransferPage = lazy(() => import('./budget/project-import/BudgetTransferPage'))

// Admin/Settings Components (still needed in restricted mode)
const UserContainer = lazy(() => import('./settings/containers/UserContainer'))
const GeneralContainer = lazy(() => import('./settings/containers/GeneralContainer'))
const RatesContainer = lazy(() => import('./settings/containers/RatesContainer'))
const UserProfileContainer = lazy(() => import('./settings/containers/UserProfileContainer'))

// Development Components
const InternalTabStandalone = lazy(() => import('./budget/components/InternalTabStandalone'))
const TestErrorBoundaryPage = lazy(() => import('./budget/pages/TestErrorBoundaryPage'))

// Contact Sales page for restricted features
const ContactSalesPage = lazy(() => import('./contact-sales/ContactSalesPage'))

interface Props {
  user: IUserDetail | null
  loggedIn: boolean
  haveRight: boolean
  checkLoader: boolean
  projectAdminRight: string[]
}

/**
 * Routes for restricted access mode.
 * Only budget-related routes and admin/profile are accessible.
 * All other routes show the ContactSalesPage.
 */
const RestrictedModeRoutes: React.FC<Props> = () => {
  return (
    <Suspense
      fallback={
        <Dimmer active inverted>
          <Loader size='big' />
        </Dimmer>
      }
    >
      <Switch>
        {/* Budget Routes - Primary functionality in restricted mode */}
        <Route exact path="/budget" component={StudyBudgetingContainer} />
        <Route exact path="/budget/transfer" component={BudgetTransferPage} />
        <Route exact path="/budget/:id([0-9]+)/detail" component={BudgetDetailContainer} />
        <Route exact path="/budget/settings" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/internal" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/external" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/units" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/templates" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/clients" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/supplier" component={BudgetSettingsContainer} />
        <Route exact path="/budget/settings/categories" component={BudgetSettingsContainer} />

        {/* Admin Routes - Still accessible in restricted mode */}
        <Route exact path="/admin" component={UserContainer} />
        <Route exact path="/admin/general" component={GeneralContainer} />
        <Route exact path="/admin/user" component={UserContainer} />
        <Route exact path="/admin/rates" component={RatesContainer} />
        <Route exact path="/profile" component={UserProfileContainer} />

        {/* Development Routes */}
        <Route exact path="/dev/internal-tab" component={InternalTabStandalone} />
        <Route exact path="/dev/error-boundary-test" component={TestErrorBoundaryPage} />

        {/* Contact Sales page */}
        <Route exact path="/contact-sales" component={ContactSalesPage} />

        {/* Restricted features - show Contact Sales page */}
        <Route path="/dashboard" component={ContactSalesPage} />
        <Route path="/user-dashboard" component={ContactSalesPage} />
        <Route path="/projects" component={ContactSalesPage} />
        <Route path="/time-tracking" component={ContactSalesPage} />
        <Route path="/accounting" component={ContactSalesPage} />
        <Route path="/reports" component={ContactSalesPage} />

        {/* Default route - redirect to budget */}
        <Route exact path="/">
          <Redirect to="/budget" />
        </Route>

        {/* Catch-all - redirect to budget */}
        <Route path="*">
          <Redirect to="/budget" />
        </Route>
      </Switch>
    </Suspense>
  )
}

export default RestrictedModeRoutes
